import { SyntheticEvent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Location, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { GAME_MODE } from '~/components/casino-shared/constants';
import { searchConversion } from '~/components/search/ducks/index';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { selectIsRegularSiteType } from '~/components/brand/ducks/index';
import { isCasinoSearchPath } from '~/utils/pathname';
import { hasDepositLoginSelector } from '~/components/casino-shared/ducks/selectors';
import { TranslationHash } from '~/components/translation/use-translations';

import { breakpointDesktop } from './game-info.styles';

const ButtonSection = styled.div`
	position: sticky;
	bottom: 0;
	width: 100%;
	background-color: var(--game-info-button-background-color);
	padding: 12px;
	z-index: 3;

	${breakpointDesktop} {
		position: static;
		background-color: transparent;
		padding: 0 12px;
		align-self: end;
		margin-bottom: 12px;
	}
`;

const PlayRealContainer = styled.div`
	width: 100%;
	height: 100%;
`;

const PlayRealButton = styled.button`
	background: var(--game-info-play-button-background-color);
	border-radius: var(--button-border-radius, 3px);
	font: var(--h3);
	color: var(--game-info-play-button-color);
	text-transform: var(--brand-text-transform, none);
	outline: none;
	width: 100%;
	height: 52px;
`;

type Props = {
	playGame: (args: { mode: string; event: SyntheticEvent; depositLogin?: boolean }) => void;
	t: TranslationHash;
};

const PlayButton = ({ playGame, t }: Props) => {
	const location = useLocation() as Location<{ gameCode: string }>;
	const dispatch = useDispatch() as (action: unknown) => void;

	const hasDepositLogin = useSelector(hasDepositLoginSelector);
	const isRegularSiteType = useSelector(selectIsRegularSiteType);
	const authenticated = useSelector(authenticationSelector);
	const gameCode = location?.state?.gameCode;

	const playButtonDataAttribute = useMemo(() => {
		if (authenticated) {
			return 'game-support-real-mode';
		}

		return isRegularSiteType ? 'game-support-real-mode' : 'game-support-deposit-login';
	}, [authenticated, isRegularSiteType]);

	const realButtonText = useMemo(() => {
		if (!isRegularSiteType && authenticated) {
			return t.PLAY_4_REAL;
		}
		if (!isRegularSiteType) {
			return t.MOBILE_TRUSTLY_DEPOSIT_LOGIN;
		}
		return `${t.PLAY}`;
	}, [isRegularSiteType, authenticated, t.PLAY_4_REAL, t.MOBILE_TRUSTLY_DEPOSIT_LOGIN, t.PLAY]);

	return (
		<ButtonSection>
			<PlayRealContainer>
				<PlayRealButton
					data-at={playButtonDataAttribute}
					onClick={e => {
						playGame({
							event: e,
							mode: GAME_MODE.REAL,
							depositLogin: hasDepositLogin
						});
						if (isCasinoSearchPath(location.pathname)) dispatch(searchConversion(gameCode));
					}}
				>
					{realButtonText}
				</PlayRealButton>
			</PlayRealContainer>
		</ButtonSection>
	);
};

export default PlayButton;
